import L, { LatLng } from 'leaflet';
import _ from 'lodash';
import PC, { union } from 'polygon-clipping';
import {
  ICalibration,
  ISpaceShape,
  LatLngMultiPolygon,
  LatLngPolygon,
  LatLngRing,
} from 'spw-redux/modules/floorplan/types';
import { typeCheck } from 'spw-shared/utils';
import { applyToPoint, Matrix } from 'transformation-matrix';
import { isNil } from 'ramda';

export const isEmptyShape = (shape: ISpaceShape) =>
  !shape ||
  !shape.polygons ||
  !shape.polygons.length ||
  (shape.polygons?.[0] && !shape.polygons?.[0]?.length) ||
  (shape.polygons?.[0]?.[0] && !shape.polygons?.[0]?.[0]?.length);

export const shapeHasCutouts = (
  shape: LatLngPolygon | LatLngMultiPolygon
): boolean => {
  if (typeCheck.isPolygon(shape)) return shape.length > 1;
  if (typeCheck.isMultiPolygon(shape))
    return shape.some((polygon) => polygon.length > 1);
  return false;
};

export const getAreaOfRing = (
  coords: LatLngRing,
  calibration: ICalibration
) => {
  if (!coords) return 0;
  const { constant } = calibration;
  let total = 0;

  for (let i = 0; i < coords.length; i++) {
    const addX = coords[i].lat / constant;
    const addY = coords[i === coords.length - 1 ? 0 : i + 1].lng / constant;
    const subX = coords[i === coords.length - 1 ? 0 : i + 1].lat / constant;
    const subY = coords[i].lng / constant;

    total += addX * addY * 0.5;
    total -= subX * subY * 0.5;
  }
  return Math.abs(total);
};

export const ensure3DimArray = (
  polygons: LatLngRing | LatLngPolygon | LatLngMultiPolygon
): LatLngMultiPolygon => {
  if (typeCheck.isMultiPolygon(polygons)) {
    return polygons as LatLngMultiPolygon;
  }
  if (typeCheck.isPolygon(polygons)) {
    return [polygons as LatLngPolygon];
  }
  return [[polygons as LatLngRing]];
};

export function multiPolygonToLatLng(
  multipolygon: PC.MultiPolygon
): LatLng[][][] {
  return multipolygon.map((polygon) =>
    polygon.map((ring) => ring.map(([x, y]) => new LatLng(y, x)))
  );
}

export function multiPolygonToLngLat(
  multipolygon: PC.MultiPolygon
): LatLng[][][] {
  return multipolygon.map((polygon) =>
    polygon.map((ring) => ring.map(([x, y]) => new LatLng(x, y)))
  );
}

export const calculatePolygonsUnion = (
  coords: LatLngMultiPolygon,
  options?: { outputFormat: 'latlng' | 'geometry' }
) => {
  const [basePolygon, ...otherPolygons] = coords;

  const basePolyCoords = L.polygon(basePolygon).toGeoJSON().geometry
    .coordinates as PC.Polygon | PC.MultiPolygon;
  const otherPolygonsCoords = L.polygon(otherPolygons).toGeoJSON().geometry
    .coordinates as PC.Polygon | PC.MultiPolygon;
  const polygonsUnion = union(
    basePolyCoords,
    otherPolygons && otherPolygons.length ? otherPolygonsCoords : basePolyCoords
  );
  return options?.outputFormat === 'geometry'
    ? polygonsUnion
    : multiPolygonToLatLng(polygonsUnion);
};

export const getAreaOfShape = (
  shape: ISpaceShape,
  calibration: ICalibration
) => {
  if (!calibration || !calibration.constant) return 0;
  if (isEmptyShape(shape)) return 0;

  let area = 0;
  if (Array.isArray(shape.polygons[0][0])) {
    const polygonsUnion = calculatePolygonsUnion(
      shape.polygons
    ) as LatLngMultiPolygon;
    area = polygonsUnion.reduce((acc, polygon, _index) => {
      const innerCalc =
        getAreaOfRing(polygon[0] as LatLngRing, calibration) - // first is enclosing polygon
        (_.sumBy(_.tail(polygon), (
          rings // all following arrays are subtractive cutouts
        ) => getAreaOfRing(rings, calibration)) || 0);
      return acc + (innerCalc || 0);
    }, 0);
  }

  return Math.round(area / 144);
};

export const isRectanglePolygon = (polygons: LatLngMultiPolygon) => {
  if (polygons?.length !== 1 || polygons?.[0]?.length !== 1) return false;
  if (polygons) {
    if (polygons[0][0]) {
      if (polygons[0][0].length === 4) {
        if (
          (polygons[0][0][0].lng === polygons[0][0][1].lng &&
            polygons[0][0][1].lat === polygons[0][0][2].lat &&
            polygons[0][0][2].lng === polygons[0][0][3].lng &&
            polygons[0][0][3].lat === polygons[0][0][0].lat) ||
          (polygons[0][0][0].lat === polygons[0][0][1].lat &&
            polygons[0][0][1].lng === polygons[0][0][2].lng &&
            polygons[0][0][2].lat === polygons[0][0][3].lat &&
            polygons[0][0][3].lng === polygons[0][0][0].lng)
        ) {
          return true;
        }
      }
    }
  }
  return false;
};

export const isRectangleShape = (shape: ISpaceShape) => {
  return isRectanglePolygon(shape?.polygons);
};

export function polygonToLngLat(polygon: PC.Polygon): LatLng[][] {
  return polygon.map((ring) => ring.map(([x, y]) => new LatLng(y, x)));
}

export function latLngsToMultiPolygonPoints(
  latlngs: LatLng[][][]
): { x: number; y: number }[][][] {
  return latlngs.map((polygon) =>
    polygon.map((ring) => ring.map(({ lat, lng }) => ({ x: lat, y: lng })))
  );
}

export function ringPointsToLatLngs(
  points: { x: number; y: number }[]
): LatLng[] {
  return points.map(({ x, y }) => new LatLng(x, y));
}

export function latLngsToMultiPolygonPointsTuple(
  latlngs: LatLng[][][]
): PC.MultiPolygon {
  return latlngs.map((polygon) =>
    polygon.map((ring) => ring.map(({ lat, lng }) => [lat, lng]))
  );
}

export function multiPolygonPointsTupleToLatLngs(
  geometry: PC.MultiPolygon
): LatLng[][][] {
  return geometry.map((polygon) =>
    polygon.map((ring) => ring.map((coord) => new LatLng(coord[0], coord[1])))
  );
}

// All shapes rotate on their own axis/center so we use polar coordinates and
// the equation of a circle to calculate the x,y coordinates of every marker
// after rotation of the selection shape
// Formulas:
// Equation of a Circle When the Centre is not an Origin: (x-h)^2+(y-k)^2 = r^2
// x and y are the coordinates of a point placed with a distance of r (radius) to the
// center of the circle, where h and k are the coordinates of the center of the circle
// ----------------------------------------------------------------------------
// Polar equation of a circle: (r cos θ)^2 + (r sin θ)^2 = a^2
// r (radius) is equal to a, θ is the angle the point against x or y axis placed in the center
// of the circle
export const calculateRotatedLatLng = (
  initialPosition: LatLng,
  shapeCenter: LatLng,
  angle: number,
  startAngle: number
): LatLng => {
  const y = initialPosition.lng;
  const x = initialPosition.lat;
  const radius = Math.sqrt(
    (x - shapeCenter.lat) ** 2 + (y - shapeCenter.lng) ** 2
  );

  const xDiff = x - shapeCenter.lat;
  const yDiff = y - shapeCenter.lng;
  let initialAngle = (Math.asin(xDiff / radius) * 180) / Math.PI;
  if (yDiff < 0) {
    initialAngle = (Math.acos(yDiff / radius) * 180) / Math.PI;
  }
  if (xDiff < 0 && yDiff < 0) {
    initialAngle = (Math.acos(-yDiff / radius) * 180) / Math.PI + 180;
  }
  const newAngle = initialAngle - (angle - startAngle);

  const newX = radius * Math.sin((newAngle * Math.PI) / 180) + shapeCenter.lat;
  const newY = radius * Math.cos((newAngle * Math.PI) / 180) + shapeCenter.lng;

  return new LatLng(newX, newY);
};

export const calculatedMovedLatLng = (
  initialPosition: LatLng,
  shapeOrigin: LatLng,
  relativeMoveOrigin: LatLng
) => {
  return new L.LatLng(
    initialPosition.lat + shapeOrigin.lat - relativeMoveOrigin.lat,
    initialPosition.lng + shapeOrigin.lng - relativeMoveOrigin.lng
  );
};

export const rotateMultiPolygon = (
  multiPolygon: L.LatLng[][][],
  shapeCenter: LatLng,
  angle: number,
  startAngle: number
) => {
  return multiPolygon.map((polygon) => {
    return polygon.map((ring) => {
      return ring.map((latlng) => {
        return calculateRotatedLatLng(latlng, shapeCenter, angle, startAngle);
      });
    });
  });
};

export const moveMultiPolygon = (
  multiPolygon: L.LatLng[][][],
  shapeOrigin: LatLng,
  relativeMoveOrigin: LatLng
) => {
  return multiPolygon.map((polygon) => {
    return polygon.map((ring) => {
      return ring.map((latlng) => {
        return calculatedMovedLatLng(latlng, shapeOrigin, relativeMoveOrigin);
      });
    });
  });
};

export const rotatePolyline = (
  polyline: L.LatLng[],
  shapeCenter: LatLng,
  angle: number,
  startAngle: number
) => {
  return polyline.map((latlng) => {
    return calculateRotatedLatLng(latlng, shapeCenter, angle, startAngle);
  });
};

export const movePolyline = (
  polyline: L.LatLng[],
  shapeOrigin: LatLng,
  relativeMoveOrigin: LatLng
) => {
  return polyline.map((latlng) => {
    return calculatedMovedLatLng(latlng, shapeOrigin, relativeMoveOrigin);
  });
};

export const applyToMultiPolygon = (
  matrix: Matrix,
  multiPolygon: L.LatLng[][][]
) => {
  return multiPolygon.map((polygon) => {
    return polygon.map((ring) => {
      return ring.map((latlng) => {
        const transformedPoint = applyToPoint(matrix, {
          x: latlng.lat,
          y: latlng.lng,
        });
        return new LatLng(transformedPoint.x, transformedPoint.y);
      });
    });
  });
};

export const applyToPolygon = (
  matrix: Matrix,
  polygon: L.LatLng[][] | L.LatLngLiteral[][]
) => {
  return polygon.map((ring) => {
    return ring.map((latlng) => {
      const transformedPoint = applyToPoint(matrix, {
        x: latlng.lat,
        y: latlng.lng,
      });
      return new LatLng(transformedPoint.x, transformedPoint.y);
    });
  });
};

export const moveMultiPolygonByPx = (
  multiPolygon: L.LatLng[][][],
  offsetInPx: number,
  map: L.Map
) => {
  const latlngOffset = map.unproject(L.point(offsetInPx, offsetInPx));
  return multiPolygon.map((polygon) => {
    return polygon.map((ring) => {
      return ring.map((latlng) => {
        const newLatLng = L.latLng([
          latlng.lat - Math.abs(latlngOffset.lat),
          latlng.lng + Math.abs(latlngOffset.lng),
        ]);
        return newLatLng;
      });
    });
  });
};

const radiansToDegrees = (radians: number) => {
  return radians * (180 / Math.PI);
};

const slope = (line: LatLng[]) => {
  const p1 = line[0];
  const p2 = line[1];
  if (p2.lng - p1.lng === 0) return Infinity;
  return (p2.lat - p1.lat) / (p2.lng - p1.lng);
};

const calculateSegmentAngle = (segment: LatLng[]) => {
  const segmentSlope = slope(segment);
  let segmentAngle;
  if (segmentSlope === Infinity) {
    segmentAngle = radiansToDegrees(Math.PI / 2);
  } else {
    segmentAngle = radiansToDegrees(Math.atan(segmentSlope));
  }
  return segmentAngle;
};

const segmentLength = (segment: LatLng[]) => {
  const p1 = segment[0];
  const p2 = segment[1];
  return Math.sqrt((p2.lat - p1.lat) ** 2 + (p2.lng - p1.lng) ** 2);
};

const findSegmentWithAngleEqualToLine = (
  line: LatLng[],
  lineAngle: number,
  segmentPoint1: LatLng,
  segmentPoint2: LatLng
) => {
  const newSegmentAngle = calculateSegmentAngle([segmentPoint1, segmentPoint2]);

  if (newSegmentAngle === lineAngle) {
    const newSegment = [segmentPoint1, segmentPoint2];
    const lineLength = segmentLength(line);
    const newSegmentLength = segmentLength(newSegment);

    if (
      newSegmentLength >= lineLength * 0.95 &&
      newSegmentLength <= lineLength * 1.05
    ) {
      return newSegment;
    }

    return line;
  }

  return null;
};

/**
 * @param {LatLng[]} line
 * @param {LatLng} point
 * @param {L.Map} map
 * @returns {Boolean} Returns true if the point is within the line segment or close to it.
 */
const isPolygonPointCloseToLine = (
  line: LatLng[],
  point: LatLng,
  map: L.Map
): boolean => {
  const p1 = line[0];
  const p2 = line[1];
  const x1 = Math.min(p1.lng, p2.lng);
  const x2 = Math.max(p1.lng, p2.lng);
  const y1 = Math.min(p1.lat, p2.lat);
  const y2 = Math.max(p1.lat, p2.lat);
  // The length of the predicted lines can be approximately 10% shorter
  // than those of the snapping layer.
  // An offset is used to find points close to the predicted line.
  // When the distance between two points is near to zero, a small percent of the width or height of
  // the image is used as the offset.
  const { lat: mapLat, lng: mapLng } = map.getBounds().getNorthEast();
  // The small height/width percentage (.2%) was chosen after testing the algorithm on several floorplans.
  const horizontalOffset = x2 - x1 <= 1 ? mapLng * 0.002 : (x2 - x1) * 0.1;
  const verticalOffset = y2 - y1 <= 1 ? mapLat * 0.002 : (y2 - y1) * 0.1;

  return (
    point.lng >= x1 - horizontalOffset &&
    point.lng <= x2 + horizontalOffset &&
    point.lat >= y1 - verticalOffset &&
    point.lat <= y2 + verticalOffset
  );
};

// See: https://paulbourke.net/geometry/pointlineplane/
const closestPointInSegment = (point: LatLng, segment: LatLng[]) => {
  const p1 = segment[0];
  const p2 = segment[1];
  const x = point.lat;
  const y = point.lng;
  const lineX1 = p1.lat;
  const lineX2 = p2.lat;
  const lineY1 = p1.lng;
  const lineY2 = p2.lng;
  const length = segmentLength(segment);

  let u =
    ((x - lineX1) * (lineX2 - lineX1) + (y - lineY1) * (lineY2 - lineY1)) /
    length ** 2;

  if (u > 1) {
    u = 1;
  } else if (u < 0) {
    u = 0;
  }
  return new LatLng(
    lineX1 + u * (lineX2 - lineX1),
    lineY1 + u * (lineY2 - lineY1)
  );
};

/**
 * @param {LatLng} point
 * @param {LatLng[]} segment
 * @returns {number} Returns the distance to the closest point in segment
 */
const distanceToSegment = (point: LatLng, segment: LatLng[]): number => {
  const u = closestPointInSegment(point, segment);
  return segmentLength([point, u]);
};

export const findClosestSegmentPoint = (
  point: LatLng,
  segment: LatLng[],
  lineAngle: number,
  map: L.Map
) => {
  let minDistance = Number.MAX_VALUE;
  const segmentAngle = calculateSegmentAngle(segment);

  // The segment is probably parallel to the line
  if (segmentAngle >= lineAngle - 4 && segmentAngle <= lineAngle + 4) {
    let pointIndex = -1;
    segment.forEach((segmentPoint: LatLng, index: number) => {
      const distance = map.distance(segmentPoint, point);
      if (distance < minDistance) {
        minDistance = distance;
        pointIndex = index;
      }
    });

    return segment[pointIndex];
  }

  return closestPointInSegment(point, segment);
};

export const isSegmentNearlyPerpendicular = (
  lineSlope: number,
  segmentSlope: number
) => {
  if (
    (lineSlope === Infinity && segmentSlope === 0) ||
    (segmentSlope === Infinity && lineSlope === 0)
  ) {
    return true;
  }

  if (lineSlope !== Infinity && segmentSlope !== Infinity) {
    const lineAngle = Math.abs(radiansToDegrees(Math.atan(lineSlope))) % 180;
    const segmentAngle = Math.abs(
      radiansToDegrees(Math.atan(segmentSlope)) % 180
    );
    // If the segment’s angle is close to the line’s angle plus 90°, they are nearly perpendicular.
    const angleDelta = 90;
    const minAngle = angleDelta - 10;

    return (
      lineAngle + minAngle <= segmentAngle &&
      lineAngle + angleDelta >= segmentAngle
    );
  }
  return false;
};

export const findClosestSegmentToPoint = (
  point: L.LatLng,
  lineSlope: number,
  polygons: LatLng[][]
) => {
  let minDistance = Number.MAX_VALUE;
  let segment;

  polygons.forEach((polygon) => {
    const vertices = polygon.length;

    for (let i = 1; i < vertices; i++) {
      const p1 = polygon[i - 1];
      const p2 = polygon[i];
      const polygonSegment = [p1, p2];
      const segmentSlope = slope(polygonSegment);

      const distance = distanceToSegment(point, polygonSegment);
      const isSegmentPerpendicular = isSegmentNearlyPerpendicular(
        lineSlope,
        segmentSlope
      );

      if (distance < minDistance && isSegmentPerpendicular) {
        minDistance = distance;
        segment = polygonSegment;
      }
    }
  });

  return segment;
};

export const findClosestLine = (
  polygons: LatLng[][],
  line: LatLng[],
  map: L.Map
): LatLng[] => {
  if (line.length <= 1) return null;

  const p1 = line[0];
  const p2 = line[1];
  const lineSlope = slope([p1, p2]);

  const polygonsWithinRatio = polygons.filter((polygon) => {
    return polygon.some((point) => isPolygonPointCloseToLine(line, point, map));
  });

  const closestSegmentToPoint1 = findClosestSegmentToPoint(
    p1,
    lineSlope,
    polygonsWithinRatio
  );

  const closestSegmentToPoint2 = findClosestSegmentToPoint(
    p2,
    lineSlope,
    polygonsWithinRatio
  );

  if (isNil(closestSegmentToPoint1) || isNil(closestSegmentToPoint2 === null))
    return null;

  const lineAngle = calculateSegmentAngle(line);

  const segmentClosestPoint1 = findClosestSegmentPoint(
    p1,
    closestSegmentToPoint1,
    lineAngle,
    map
  );

  const segmentClosestPoint2 = findClosestSegmentPoint(
    p2,
    closestSegmentToPoint2,
    lineAngle,
    map
  );

  return findSegmentWithAngleEqualToLine(
    line,
    lineAngle,
    segmentClosestPoint1,
    segmentClosestPoint2
  );
};
